import { ComponentType, LazyExoticComponent, lazy } from 'react';
import { localStorageKeys } from '../utils/storage';

const isChunkLoadError = (error: unknown): boolean => {
  const { name, message } = (error ?? {}) as { name?: string; message?: string };
  return name === 'ChunkLoadError' || /Loading chunk|dynamically imported module|Failed to fetch/i.test(message || '');
};

export const lazyWithRetry = <T extends ComponentType>(
  componentImport: () => Promise<{ default: T }>,
): LazyExoticComponent<T> =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem(localStorageKeys.forcePageRefresh) || 'false',
    );

    try {
      const component = await componentImport();
      window.localStorage.setItem(localStorageKeys.forcePageRefresh, 'false');
      return component;
    } catch (error) {
      if (isChunkLoadError(error) && !pageHasAlreadyBeenForceRefreshed) {
        // Assuming that the user is not on the latest version of the application.
        // Let's refresh the page immediately.
        window.localStorage.setItem(localStorageKeys.forcePageRefresh, 'true');
        window.location.reload();
        // Keep Suspense pending while the reload happens so React never tries
        // to render the undefined result of reload().
        return new Promise<{ default: T }>(() => {});
      }

      // Either a genuine error (not a stale chunk) or the page has already been
      // reloaded. Assume the user is on the latest version and let it crash.
      throw error;
    }
  });
